import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Mic, Video, X } from 'lucide-react';
import { recordSyllabusSession } from '../../api';
import showToast from '../../utils/toast';
import './PracticeRecordModal.css';

const MB = 1024 * 1024;
// What the browser records, per kind: the recorder formats the server takes (backend
// constants/syllabusSubmission.js), the longest recording and the largest file it takes. The same as
// the Coins app's Practice record (coins-app/src/pages/PracticeRecord.jsx).
const KINDS = {
  audio: {
    label: 'Audio',
    Icon: Mic,
    types: ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'],
    constraints: { audio: true },
    quality: { audioBitsPerSecond: 128000 },
    maxSeconds: 10 * 60,
    maxBytes: 30 * MB,
    device: 'microphone',
  },
  video: {
    label: 'Video',
    Icon: Video,
    types: ['video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'],
    // The back camera on a phone or tablet: the child's work or hands, not their face.
    constraints: { audio: true, video: { facingMode: 'environment' } },
    quality: { videoBitsPerSecond: 1500000 },
    maxSeconds: 5 * 60,
    maxBytes: 100 * MB,
    device: 'camera',
  },
};
const EXTENSIONS = { 'audio/webm': 'webm', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg', 'video/webm': 'webm', 'video/mp4': 'mp4' };
const baseType = (type) => String(type || '').split(';')[0].trim().toLowerCase();
const supportedType = (kind) =>
  typeof MediaRecorder.isTypeSupported === 'function'
    ? KINDS[kind].types.find((type) => MediaRecorder.isTypeSupported(type)) || ''
    : '';
const canRecord = () => typeof window.MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
const clock = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
const CANT_RECORD = 'This browser can’t record here. Open the page in Chrome, Edge or Safari, or give coins without a recording.';

const deviceError = (error, kind) => {
  const { device } = KINDS[kind];
  const name = error?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return `Allow the ${device} for this site, then try again.`;
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return `No ${device} found on this device.`;
  if (name === 'NotReadableError') return `The ${device} is busy in another app. Close it and try again.`;
  return `Couldn’t turn on the ${device}. Try again.`;
};

/**
 * Syllabus Tracker "Practice record", opened by clicking a child's score: before giving coins the coach
 * can record audio or video of the child's practice. Saved, it goes to S3 as one of the child's
 * submissions for the cell (recordSyllabusSession) and onGrade(submission) opens the grading for it.
 * "Give coins without a recording" calls onGrade() straight away, for the score editor as before.
 * `details` = the lines under the child's name; `scope` = { balagruhaId, topic, month, year }.
 */
export default function PracticeRecordModal({ child, sessionNumber, details, scope, onClose, onGrade }) {
  const titleId = useId();
  const closeRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(0);
  const urlRef = useRef(null);
  // Which camera / microphone request is the current one (Back or closing drops a pending one).
  const attemptRef = useRef(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [kind, setKind] = useState(null); // null = choosing audio or video
  // starting (asking for the device), ready, recording, recorded, saving, failed
  const [phase, setPhase] = useState('starting');
  const [seconds, setSeconds] = useState(0);
  const [take, setTake] = useState(null); // { blob, url, type }
  const [error, setError] = useState('');
  const option = kind && KINDS[kind];

  const stopDevice = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);
  // Throws the recording in progress away.
  const dropRecorder = useCallback(() => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== 'inactive') {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.stop();
    }
  }, []);
  const clearTake = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
    setTake(null);
  }, []);
  // Stops the recording in progress: onstop keeps it.
  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  // Closing turns the camera / microphone off and forgets the recording.
  useEffect(
    () => () => {
      attemptRef.current += 1;
      dropRecorder();
      stopDevice();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [dropRecorder, stopDevice]
  );

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // The camera's picture while framing and recording.
  useEffect(() => {
    if (kind === 'video' && videoRef.current && streamRef.current && (phase === 'ready' || phase === 'recording')) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [kind, phase]);

  // The clock, and the stop at the longest recording.
  useEffect(() => {
    if (phase !== 'recording') return undefined;
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedRef.current) / 1000);
      setSeconds(elapsed);
      if (elapsed >= KINDS[kind].maxSeconds) stop();
    }, 250);
    return () => clearInterval(timer);
  }, [phase, kind, stop]);

  const openDevice = async (next) => {
    const attempt = ++attemptRef.current;
    dropRecorder();
    stopDevice();
    clearTake();
    setKind(next);
    setError('');
    setSeconds(0);
    if (!canRecord()) {
      setPhase('failed');
      setError(CANT_RECORD);
      return;
    }
    setPhase('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia(KINDS[next].constraints);
      if (attempt !== attemptRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      setPhase('ready');
    } catch (failure) {
      if (attempt !== attemptRef.current) return;
      setPhase('failed');
      setError(deviceError(failure, next));
    }
  };

  const start = () => {
    const stream = streamRef.current;
    if (!stream) return;
    const mimeType = supportedType(kind);
    let recorder;
    try {
      recorder = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), ...KINDS[kind].quality });
    } catch {
      stopDevice();
      setPhase('failed');
      setError(CANT_RECORD);
      return;
    }
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      const type = baseType(recorder.mimeType || mimeType) || `${kind}/webm`;
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      stopDevice();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setTake({ blob, url, type });
      setPhase('recorded');
    };
    recorder.start(1000);
    recorderRef.current = recorder;
    startedRef.current = Date.now();
    setSeconds(0);
    setPhase('recording');
  };

  const backToChoose = () => {
    attemptRef.current += 1;
    dropRecorder();
    stopDevice();
    clearTake();
    setKind(null);
    setError('');
  };

  const save = async () => {
    const { label, maxBytes } = option;
    if (take.blob.size > maxBytes) {
      setError(`This ${label.toLowerCase()} is too big to send (over ${maxBytes / MB} MB). Record a shorter one.`);
      return;
    }
    setPhase('saving');
    setError('');
    try {
      const file = new File([take.blob], `practice-${kind}.${EXTENSIONS[take.type] || 'webm'}`, { type: take.type });
      const result = await recordSyllabusSession(child.studentId, sessionNumber, { ...scope, submissionType: kind, file });
      showToast(`${label} saved for ${child.name}. Now give the coins.`, 'success');
      onGrade(result?.submission || null);
    } catch (failure) {
      setPhase('recorded');
      setError(
        failure?.response
          ? failure.response.data?.message || `Couldn’t save the ${label.toLowerCase()}. Try again.`
          : `Couldn’t send the ${label.toLowerCase()}: check the internet connection and try again, or give coins without it.`
      );
    }
  };

  const live = phase === 'ready' || phase === 'recording';
  const kept = take && (phase === 'recorded' || phase === 'saving');

  return (
    // A click outside closes it only while choosing, so a recording isn't lost by accident.
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(event) => event.target === event.currentTarget && !kind && onClose()}
    >
      <section className="prm-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="prm-head">
          <div>
            <p className="prm-eyebrow">Practice record</p>
            <h2 id={titleId}>Record a little progress</h2>
          </div>
          <button ref={closeRef} type="button" className="prm-close" aria-label="Close" onClick={onClose}>
            <X size={22} />
          </button>
        </div>

        <div className="prm-child">
          <strong>{child.name}</strong>
          {details.filter(Boolean).map((line) => (
            <span key={line}>{line}</span>
          ))}
        </div>

        {!kind ? (
          <>
            <p className="prm-lead">Choose how to capture this practice.</p>
            <div className="prm-options">
              {Object.entries(KINDS).map(([key, { label, Icon }]) => (
                <button key={key} type="button" className="prm-option" onClick={() => openDevice(key)}>
                  <Icon size={28} />
                  {label} recording
                </button>
              ))}
            </div>
            <p className="prm-note">For video, frame the child’s work or hands. Follow ISF’s no-face policy.</p>
            <button type="button" className="prm-skip" onClick={() => onGrade(null)}>
              Give coins without a recording →
            </button>
            <div className="prm-actions">
              <button type="button" className="prm-ghost" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="prm-lead prm-lead--kind">
              <option.Icon size={18} />
              {option.label} recording
            </p>
            {kind === 'video' && live && (
              <video ref={videoRef} className="prm-preview" autoPlay muted playsInline aria-label="Camera preview" />
            )}
            {kind === 'audio' && live && (
              <div className={`prm-mic${phase === 'recording' ? ' is-live' : ''}`} aria-hidden="true">
                <Mic size={40} />
              </div>
            )}
            {phase === 'starting' && <p className="prm-status">Turning on the {option.device}…</p>}
            {phase === 'ready' && (
              <p className="prm-status">
                {kind === 'video' ? 'Frame the child’s work or hands, then start.' : 'Ready. Start when the child begins.'}
              </p>
            )}
            {phase === 'recording' && (
              <p className="prm-timer">
                <span className="prm-dot" aria-hidden="true" />
                Recording {clock(seconds)}
                <span className="prm-limit">/ {clock(option.maxSeconds)}</span>
              </p>
            )}
            {kept &&
              (kind === 'video' ? (
                <video src={take.url} controls playsInline className="prm-preview" aria-label="Your video" />
              ) : (
                <audio src={take.url} controls className="prm-playback" aria-label="Your audio" />
              ))}
            {error && (
              <div className="prm-error" role="alert">
                {error}
              </div>
            )}

            <div className="prm-actions">
              {phase === 'recording' ? (
                <button type="button" className="prm-primary" onClick={stop}>
                  Stop
                </button>
              ) : kept ? (
                <>
                  <button type="button" className="prm-ghost" disabled={phase === 'saving'} onClick={() => openDevice(kind)}>
                    Record again
                  </button>
                  <button type="button" className="prm-primary" disabled={phase === 'saving'} onClick={save}>
                    {phase === 'saving' ? 'Saving…' : 'Save & give coins'}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="prm-ghost" onClick={backToChoose}>
                    Back
                  </button>
                  {phase === 'failed' ? (
                    <button type="button" className="prm-primary" onClick={() => onGrade(null)}>
                      Give coins without it
                    </button>
                  ) : (
                    <button type="button" className="prm-primary" disabled={phase !== 'ready'} onClick={start}>
                      Start recording
                    </button>
                  )}
                </>
              )}
            </div>
            {phase === 'recorded' && error && (
              <button type="button" className="prm-skip" onClick={() => onGrade(null)}>
                Give coins without the recording →
              </button>
            )}
          </>
        )}
      </section>
    </div>
  );
}
